import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Etiqueta, type ItemComputo } from "@/lib/api";
import {
  aPayloadItemObra,
  formItemObra,
  opcionesUnidad,
  subtotalesEnUsd,
  subtotalesForm,
  validarItemObra,
  type FormItemObra,
  type PayloadItemObra,
} from "@/lib/presupuesto";
import { agregarEtiqueta, opcionesEtiquetaForm, quitarEtiqueta } from "@/lib/proveedores";

const SIN_ERRORES: Partial<Record<keyof FormItemObra, string>> = {};
type Dialogo = { tipo: "form"; rubroId: number; item: ItemComputo | null } | { tipo: "borrar"; rubroId: number; item: ItemComputo } | null;

interface Opciones {
  /** El tipo de cambio del presupuesto (ARS por USD), para el subtotal en USD en vivo. */
  tipoCambio: string | null;
  /** Después de cada cambio guardado en un rubro: volver a pedir su ficha y el listado. */
  alCambiar: (rubroId: number) => void;
}

/**
 * Edición de los ítems y las etiquetas de un rubro del presupuesto (sin JSX), para usar en cualquier pantalla que
 * muestre rubros: alta, edición y baja de ítems (precios en pesos, con el subtotal en pesos y en USD en vivo con el
 * tipo de cambio del presupuesto; un ítem todavía en USD no deja tocar sus precios) y las etiquetas de la agenda que
 * lo cubren (cada cambio se guarda al toque, con la lista completa). Escribe sólo el admin. La UI está en
 * `EdicionRubro.tsx` (`DialogosItem`, `EtiquetasRubro`).
 */
export function useEdicionRubro({ tipoCambio, alCambiar }: Opciones) {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/presupuesto`;
  const esAdmin = membresia.rol === "admin";

  const catalogo = useApiResource<Etiqueta[]>(esAdmin ? `/api/fideicomisos/${fid}/etiquetas` : null);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormItemObra>(() => formItemObra());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  // Las etiquetas se editan por rubro: el error (y el "guardando") es del último rubro tocado.
  const [rubroEtiquetas, setRubroEtiquetas] = useState<number | null>(null);
  const [errorEtiqueta, setErrorEtiqueta] = useState<string | null>(null);

  const accionGuardar = useAsyncAction((rubroId: number, item: ItemComputo | null, datos: PayloadItemObra) =>
    item ? api.patch<ItemComputo>(`${base}/items/${item.id}`, datos) : api.post<ItemComputo>(`${base}/rubros/${rubroId}/items`, datos),
  );
  const accionBorrar = useAsyncAction((itemId: number) => api.delete<void>(`${base}/items/${itemId}`).then(() => true));
  const accionEtiquetas = useAsyncAction((rubroId: number, etiquetas: string[]) => api.put<string[]>(`${base}/rubros/${rubroId}/etiquetas`, { etiquetas }));

  const errores = validarItemObra(form);
  const subtotales = subtotalesForm(form);
  // Un ítem cargado antes del presupuesto en pesos tiene los precios en USD hasta que se carga el tipo de cambio.
  const precioEnUsd = dialogo?.tipo === "form" && dialogo.item?.moneda === "USD";
  const cerrar = () => setDialogo(null);

  const abrirForm = (rubroId: number, item: ItemComputo | null) => {
    setForm(formItemObra(item ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", rubroId, item });
  };

  const abrirBorrar = (rubroId: number, item: ItemComputo) => {
    accionBorrar.limpiarError();
    setDialogo({ tipo: "borrar", rubroId, item });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.rubroId, dialogo.item, aPayloadItemObra(form))) {
      cerrar();
      alCambiar(dialogo.rubroId);
    }
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.item.id)) {
      cerrar();
      alCambiar(dialogo.rubroId);
    }
  };

  const guardarEtiquetas = async (rubroId: number, nuevas: string[]) => {
    setRubroEtiquetas(rubroId);
    if (await accionEtiquetas.run(rubroId, nuevas)) {
      alCambiar(rubroId);
      catalogo.recargar(); // pudo haber una etiqueta nueva
    }
  };

  const sumarEtiqueta = (rubroId: number, actuales: string[], texto: string) => {
    const r = agregarEtiqueta(actuales, texto, (catalogo.data ?? []).map((e) => e.nombre));
    setRubroEtiquetas(rubroId);
    setErrorEtiqueta(r.error);
    if (r.etiquetas !== actuales) void guardarEtiquetas(rubroId, r.etiquetas);
  };

  return {
    esAdmin,
    dialogo,
    cerrar,
    abrirNuevo: (rubroId: number) => abrirForm(rubroId, null),
    abrirEditar: (rubroId: number, item: ItemComputo) => abrirForm(rubroId, item),
    abrirBorrar,
    form,
    cambiar: <K extends keyof FormItemObra>(campo: K, valor: FormItemObra[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    opcionesUnidad: opcionesUnidad(form.unidad),
    subtotales,
    subtotalesUsd: precioEnUsd ? subtotales : subtotalesEnUsd(subtotales, tipoCambio),
    precioEnUsd,
    tipoCambio,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    opcionesEtiqueta: (actuales: string[]) => opcionesEtiquetaForm(catalogo.data ?? [], actuales),
    sumarEtiqueta,
    quitarEtiqueta: (rubroId: number, actuales: string[], nombre: string) => void guardarEtiquetas(rubroId, quitarEtiqueta(actuales, nombre)),
    guardandoEtiquetas: (rubroId: number) => accionEtiquetas.loading && rubroEtiquetas === rubroId,
    errorEtiqueta: (rubroId: number) => (rubroEtiquetas === rubroId ? (errorEtiqueta ?? accionEtiquetas.error) : null),
  };
}

export type EdicionRubro = ReturnType<typeof useEdicionRubro>;
